import { useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';

import {
    type CategoryBreakdownDto,
    type ProductsResponseDto,
    type SavingsInsightsSummaryDto,
    getReportSavingsLastRequestParams,
    normalizeSavingsProductsResponse,
} from 'entities/ReportSavingsSummary';
import { $api } from 'shared/api/api';

type SavingsDrillInDimension = 'property' | 'category';

interface SavingsInsightsRequestBody {
    dateFrom: string;
    dateTo: string;
    propertyIds?: string[];
    categoryIds?: string[];
}

interface UseSavingsSummaryDrillInDataParams {
    dimension: SavingsDrillInDimension;
    id: string | undefined;
}

interface UseSavingsSummaryDrillInDataResult {
    loading: boolean;
    summary: SavingsInsightsSummaryDto | null;
    byCategory: CategoryBreakdownDto[];
    products: ProductsResponseDto | null;
    totalSaved: number;
    productsCount: number;
    orderCount: number;
    dateFrom: string;
    dateTo: string;
    datePreset?: string;
    lastRequestParamsPresent: boolean;
}

export const toSafeNumber = (value: string | number | undefined): number => {
    if (typeof value === 'number') {
        return Number.isNaN(value) ? 0 : value;
    }

    const parsed = Number.parseFloat(value ?? '');
    return Number.isNaN(parsed) ? 0 : parsed;
};

const getSavedProductsCount = (
    products: ProductsResponseDto | null,
): number => {
    if (!products?.items?.length) {
        return 0;
    }

    return products.items.filter((item) => toSafeNumber(item.totalSaved) > 0)
        .length;
};

const buildRequestBody = ({
    dimension,
    id,
    lastRequestParams,
}: {
    dimension: SavingsDrillInDimension;
    id: string;
    lastRequestParams: NonNullable<
        ReturnType<typeof getReportSavingsLastRequestParams>
    >;
}): SavingsInsightsRequestBody => {
    const baseRequestBody: SavingsInsightsRequestBody = {
        dateFrom: lastRequestParams.dateFrom,
        dateTo: lastRequestParams.dateTo,
    };

    if (dimension === 'property') {
        return {
            ...baseRequestBody,
            propertyIds: [id],
            ...(lastRequestParams.categoryIds?.length
                ? { categoryIds: lastRequestParams.categoryIds }
                : {}),
        };
    }

    return {
        ...baseRequestBody,
        categoryIds: [id],
        ...(lastRequestParams.propertyIds?.length
            ? { propertyIds: lastRequestParams.propertyIds }
            : {}),
    };
};

export const useSavingsSummaryDrillInData = ({
    dimension,
    id,
}: UseSavingsSummaryDrillInDataParams): UseSavingsSummaryDrillInDataResult => {
    const lastRequestParams = useSelector(getReportSavingsLastRequestParams);

    const [loading, setLoading] = useState(false);
    const [summary, setSummary] = useState<SavingsInsightsSummaryDto | null>(
        null,
    );
    const [byCategory, setByCategory] = useState<CategoryBreakdownDto[]>([]);
    const [products, setProducts] = useState<ProductsResponseDto | null>(null);

    const requestBody = useMemo<SavingsInsightsRequestBody | null>(() => {
        if (!lastRequestParams || !id) {
            return null;
        }

        return buildRequestBody({
            dimension,
            id,
            lastRequestParams,
        });
    }, [dimension, id, lastRequestParams]);

    useEffect(() => {
        if (!requestBody) {
            return;
        }

        let cancelled = false;

        const load = async () => {
            setLoading(true);

            try {
                const [summaryResponse, byCategoryResponse, productsResponse] =
                    await Promise.all([
                        $api.post<SavingsInsightsSummaryDto>(
                            '/v1/reports/savings-insights/summary',
                            requestBody,
                        ),
                        $api.post<{ items: CategoryBreakdownDto[] }>(
                            '/v1/reports/savings-insights/by-category',
                            requestBody,
                        ),
                        $api.post<ProductsResponseDto>(
                            '/v1/reports/savings-insights/products',
                            {
                                ...requestBody,
                                orderBy: '-total_saved',
                                page: 1,
                                pageSize: 25,
                                excludeZeroSavings: true,
                            },
                        ),
                    ]);

                if (cancelled) {
                    return;
                }

                setSummary(summaryResponse.data);
                setByCategory(byCategoryResponse.data.items ?? []);
                setProducts(
                    normalizeSavingsProductsResponse(productsResponse.data),
                );
            } catch {
                if (cancelled) {
                    return;
                }

                setSummary(null);
                setByCategory([]);
                setProducts(null);
            } finally {
                if (!cancelled) {
                    setLoading(false);
                }
            }
        };

        void load();

        return () => {
            cancelled = true;
        };
    }, [requestBody]);

    const totalSaved = useMemo(
        () => toSafeNumber(summary?.savingsAmount),
        [summary?.savingsAmount],
    );
    const productsCount = useMemo(
        () => getSavedProductsCount(products),
        [products],
    );
    const orderCount = summary?.orderCount ?? 0;

    const dateFrom = lastRequestParams?.dateFrom ?? '';
    const dateTo = lastRequestParams?.dateTo ?? '';

    return {
        loading,
        summary,
        byCategory,
        products,
        totalSaved,
        productsCount,
        orderCount,
        dateFrom,
        dateTo,
        datePreset: lastRequestParams?.datePreset,
        lastRequestParamsPresent: Boolean(lastRequestParams),
    };
};
